import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { UserPlus, Users, Wand2 } from 'lucide-react';
import api from '../lib/axios';
import { generatePassword, isPasswordValid } from '../lib/password';
import CollapsibleCard from './CollapsibleCard';

interface Member {
  userId: string;
  email: string;
  name: string | null;
  role: 'ACCOUNTANT' | 'ADMIN' | 'SUPER_ADMIN';
  active: boolean;
}

type AssignableRole = 'ACCOUNTANT' | 'ADMIN';

type MemberForm = { email: string; name: string; role: AssignableRole; password: string };

const EMPTY: MemberForm = { email: '', name: '', role: 'ACCOUNTANT', password: '' };
const ASSIGNABLE_ROLES: AssignableRole[] = ['ACCOUNTANT', 'ADMIN'];

/** Team management for an organization administrator (current tenant). */
export default function TeamMembersCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<MemberForm>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['admin-members'],
    queryFn: () => api.get<Member[]>('/admin/members').then((r) => r.data),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<Member>('/admin/members', {
        email: form.email.trim(),
        name: form.name.trim() || null,
        role: form.role,
        password: form.password.trim() || null,
      }),
    onSuccess: () => {
      setForm({ ...EMPTY });
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin-members'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('members.addError')),
  });

  const changeRole = useMutation({
    mutationFn: (vars: { userId: string; role: AssignableRole }) =>
      api.patch(`/admin/members/${vars.userId}/role`, { role: vars.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-members'] }),
    onError: (e: any) => setError(e?.response?.data?.message ?? t('members.addError')),
  });

  const submit = () => {
    setError(null);
    if (!form.email.trim()) {
      setError(t('members.missingEmail'));
      return;
    }
    if (form.password.trim() && !isPasswordValid(form.password.trim())) {
      setError(t('members.passwordPolicy'));
      return;
    }
    add.mutate();
  };

  const fillGeneratedPassword = () => {
    setError(null);
    setForm((f) => ({ ...f, password: generatePassword() }));
  };

  const list = members.data ?? [];

  return (
    <CollapsibleCard
      className="mb-6 max-w-2xl"
      title={
        <span className="flex items-center gap-2">
          <Users size={18} className="text-ink-muted" /> {t('members.teamTitle')}
        </span>
      }
    >
      <p className="mb-3 text-sm text-ink-muted">{t('members.teamSubtitle')}</p>

      <div className="mb-3 rounded-lg bg-surface-raised p-3">
        <p className="eyebrow flex items-center gap-2">
          <UserPlus size={14} /> {t('members.add')}
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="field-label">{t('members.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="input"
              placeholder="membre@association.fr"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="field-label">{t('members.name')}</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="input"
              placeholder={t('members.namePlaceholder')}
            />
          </div>
          <div>
            <label className="field-label">{t('members.role')}</label>
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as AssignableRole }))}
              className="input"
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`members.roles.${role}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label">{t('members.password')}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="input flex-1"
                placeholder={t('members.passwordHint')}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={fillGeneratedPassword}
                className="btn btn-ghost shrink-0"
                title={t('members.generatePassword')}
              >
                <Wand2 size={16} /> {t('members.generate')}
              </button>
            </div>
            <p className="mt-1 text-xs text-ink-muted">{t('members.passwordPolicy')}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1" />
          {error && <span className="text-sm font-medium text-expense">{error}</span>}
          <button onClick={submit} disabled={add.isPending} className="btn btn-primary">
            {add.isPending ? t('members.adding') : t('members.add')}
          </button>
        </div>
      </div>

      {members.isLoading && <p className="px-3 pb-3 text-ink-muted">{t('common.loading')}</p>}
      {!members.isLoading && list.length === 0 && (
        <p className="px-3 pb-3 text-sm text-ink-muted">{t('members.empty')}</p>
      )}

      {list.length > 0 && (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('members.email')}</th>
                <th>{t('members.name')}</th>
                <th>{t('members.role')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((member) => (
                <tr key={member.userId}>
                  <td data-label={t('members.email')} className="font-medium text-ink">
                    {member.email}
                  </td>
                  <td data-label={t('members.name')} className="text-ink-soft">
                    {member.name || t('common.none')}
                  </td>
                  <td data-label={t('members.role')} className="cell-block">
                    {member.role === 'SUPER_ADMIN' ? (
                      <span className="pill pill-neutral">{t('members.roles.SUPER_ADMIN')}</span>
                    ) : (
                      <select
                        value={member.role}
                        onChange={(e) =>
                          changeRole.mutate({
                            userId: member.userId,
                            role: e.target.value as AssignableRole,
                          })
                        }
                        disabled={changeRole.isPending}
                        className="input !py-1"
                      >
                        {ASSIGNABLE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {t(`members.roles.${role}`)}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleCard>
  );
}
